import {useState, useEffect} from 'react';
import {Cloudinary} from '@cloudinary/url-gen';
import {thumbnail} from '@cloudinary/url-gen/actions/resize';
import { useDispatch, useSelector } from 'react-redux';
import config from 'config';
import { htToast } from 'HTKit/Toast';

/* hooks */
import { useSignedToken } from 'src/components/Cloudinary/apis/api.signedToken';

/* Constants/Utils  */
import { getFilesGroupedByMaxFileSize, keysToSnakeCase } from 'src/components/Cloudinary/cloudinary.utils';
import useAPI from 'src/hooks/useAPI';
import { logger } from 'src/utils/logger';
import {ALLOWED_FILES} from 'src/components/Cloudinary/cloudinary.constants';
import {shortenStringByNumber} from 'src/utils/text';

/* Queries / Ducks */
import {setFileUploadSuccess, deleteFileUploadSuccess} from 'src/components/Cloudinary/cloudinary.actions';

/* Selectors */
import { pureUserSelector } from 'src/selectors/user';
// import {notifications} from 'components/Notification/notification.ducks';

/**
 * Clouding hook to allow for uploading, transforming, deleting, and signing files. There are two mechanisms for
 *    for state. Local & redux. This depends on the use case if your upload icon sits in the same space as your image
 *    display. If it does, you can use local state. If it doesn't, you can use redux state.
 *
 * @param namespace - If images are attached to an Editor. Same namespace as editor. Not exclusive to editor.
 *                    If you need to save in redux, also pass in namespace.
 * @param height - Height of transformed image to display
 * @param width - Width of transformed width to display
 */
const useCloudinaryHooks = ({
  /* If images are attached to an Editor. Same namespace as editor */
  namespace,
  /* Height of transformed image to display */
  height = 48,
  /* Width of transformed width to display */
  width = 48,
  /* We could put this in config, but it could be a variance so let the usage dictate */
  folder = '',
}) => {
  /* Local State */
  const [uploadedSuccessFiles, setUploadedSuccessFiles] = useState();
  const [uploadErrorFiles, setUploadErrorFiles] = useState();
  const [cloudinaryLoading, setLoading] = useState(false);
  const [totalSuccessFilesCount, setTotalSuccessFilesCount] = useState(0);

  /* Hooks */
  const generateCloudinaryAuthMutation = useSignedToken();
  const dispatch = useDispatch();
  const APIS = useAPI();

  /* Selectors */
  const userCurrent = useSelector(pureUserSelector);

  /* Constants */

  /* Common params for APIs */
  const params = {
    type: 'authenticated',
    access_control: JSON.stringify([{access_type: 'token'}]),
    context: `ht_owner_id=${userCurrent?.id}`,
    ...folder && ({folder})
  };

  /* -------------------------------------------------------------------------------- */
  /* ---------------------- STATE: Local & Redux Actions ---------------------------- */
  /* -------------------------------------------------------------------------------- */

  /**
   * This just consolidates two behaviors to keep parity with state.
   * We only want to update state if the file does not already exist.
   *    - This will happen for seeded data
   *
   * 1. setFileUploadReduxSuccess
   * 2. handleUploadSuccess
   */
  const consolidateStateFileSuccessUpload = (file) => {
    setFileUploadReduxSuccess(file);
    handleUploadSuccess(file);
  }
  /**
   * Set redux state for successful uploads
   * @param data
   */
  const setFileUploadReduxSuccess = (data) => {
    if (namespace) {
      dispatch(
        setFileUploadSuccess({
          success: data,
          append: true,
          editorNamespace: namespace,
        })
      );
    }
  };

  /**
   * Set redux state for failed uploads or file size errors
   * Note: Not wired up. Not needed at this time.
   * @param data
   */
  const setFileUploadReduxError = (data) => {
    if (namespace) {
      dispatch(
        setFileUploadSuccess({
          error: data,
          append: false,
          editorNamespace: namespace,
        })
      );
    }
  };

  /**
   * Set local state for total number of successful uploads
   * - remove dupes that can happen from seeding values
   * @param result
   */
  const handleUploadSuccess = (result) => {
    setUploadedSuccessFiles((prevFiles) => {
      const transformedSuccess = keysToSnakeCase(result);
      const prevState = prevFiles || [];
      /* If we don't have preseeded files, no dupes, concat them */
      if (!prevState.some(f => f.public_id === transformedSuccess.public_id)) {
        return prevState.concat(transformedSuccess);
      };

      return prevState;
    });
  };

  /**
   * Update local and global concerns. All files have been either errored' or uplaoded.
   * So, Remove them from state.
   *
   * When: when user "saves" from the editor or "cancels" from the editor
   */
  const handleRemoveSuccessFiles = () => {
    /* 1. clear local */
    setUploadedSuccessFiles([]);

    /* 2. clear redux */
    if (namespace) {
      dispatch(
        setFileUploadSuccess({
          editorNamespace: namespace,
          append: false,
          success: [],
        })
      );
    }
  };

  /* Update total success files on deck to be uploaded. Can't ever be below 0 */
  const handleSetTotalSuccessFilesCount = (total) => {
    if (total)  {
      setTotalSuccessFilesCount(total);
    } else {
      // lets use previous settings
      setTotalSuccessFilesCount(prevTotal => {
        if (prevTotal > 0) {
          return prevTotal - 1;
        }
        return 0;
      });
    }
  };
  /* -------------------------------------------------------------------------------- */
  /* -------------------- MISCELLANEOUS CLOUDINARY METHODS -------------------------- */
  /* -------------------------------------------------------------------------------- */

  /* Get a new cloudinary instance */
  const newCloudinary = new Cloudinary({
    cloud: {
      cloudName: config.cloudinary.cloudName,
    },
  });

  /* Transform the image, simply. Just by height/width. Many options available */
  const imageTransformation = (image) => {
    image.resize(thumbnail().width(width).height(height)).setDeliveryType('authenticated');
  };

  /* This uses the rails as a proxy and hands back our pathing for the cloudinary transformed image */
  const getTransformedUrl = async (urls) => {
    const paths = Array.isArray(urls) ? urls : [urls];
    try {
      const response = await APIS.cloudinary.getFileAssets({assets_paths: paths});

      /* We are assuming a single transform at one time. We can update to multi */
      // eslint-disable-next-line camelcase
      return response.data?.signed_urls[0];
    } catch (e) {
      logger('Get Transformed URL: ')(e);
      return null;
    }
  };

  /**
   * Transform the image. This is a single transform. We'd use this outside of the editor usage.
   * This should be used in conjuntion with our Thumbnail component.
   *
   * @param file
   * @param options - {h,w}, representing height/width. Only needed if you are using this with an editor and
   *                  need flexibility in variation. Otherwise, just pass it in when creating the hook.
   *
   * @returns {string[]} - Array of transformed urls with their original url}
   */
  const handleTransformImage = async (file) => {
    const transformFiles = Array.isArray(file) ? file : [file];
    /* 1. Number of transforms raw and image we want the thumbnail component to fake preload */
    setTotalSuccessFilesCount(file.length);

    /* 2. Kick off the promise maps */
    transformFiles.map(async (f) => {
      /* 3. Only transform images. Raw passes thru */
      if (f.resource_type === 'image') {
        const transformedImage = await getAssetImagePath(f);
        // eslint-disable-next-line no-param-reassign
        f.transformed_image = transformedImage;
      }
      /* 4. Retrieval (fail or success) so no need to fake preload */
      handleSetTotalSuccessFilesCount();

      /* 5. Update state, local & global */
      consolidateStateFileSuccessUpload(f);

      return f;
    });
  };

  /* We only care about image at this time, to tranform. Raw file types get icon displays */
  const getAssetImagePath = async (data) => {
    const image = newCloudinary.image(data.public_id);

    // Apply the transformation.
    await imageTransformation(image);

    const url = new URL(image.toURL());

    const transformedImageUrl = await getTransformedUrl(url.pathname);
    return transformedImageUrl;
  };

  /* -------------------------------------------------------------- */
  /* -------------------- DELETE ACTIONS -------------------------- */
  /* -------------------------------------------------------------- */

  /*
    Delete from the server: We need a rails wrapper for this. Used for single item deletion. Usually from a delete icon: TODO
    Note: Deletes on file at a time.
  */
  const deleteCloudinaryFiles = async (files) => {
    // eslint-disable-next-line camelcase
    const deletePromises = files.map(({type, public_id}) => APIS.cloudinary.deleteResource({type, public_id}));

    /* We don't care if get a fail, lets delete as many as we can */
    const allDeleteResponses = await Promise.allSettled(deletePromises);
    // @ts-ignore
    const hasErrors = allDeleteResponses.some(filterError => filterError.value?.err);

    /* If erroring, log it. */
    if (hasErrors) {
      logger('Delete Cloudinary Files: ')('');
    }
  };

  /* Delete all cloudinary assets associated with current interaction/session (not saved yet) */
  const handleDeleteAllFromCloudinary = async () => {
    if (!uploadedSuccessFiles || uploadedSuccessFiles.length === 0) return;

    /* 1. Batch delete */
    await deleteCloudinaryFiles(uploadedSuccessFiles);

    /* 2. Lets delete from state successes */
    handleRemoveSuccessFiles();
  };

  /*
     Delete Single Asset from client & cloudinary*: Clear our all state & remove from cloudinary.
     Note: Used for a single item deletion. Ie. delete icon on a thumbnail

     public
  */
   const handleDeleteFile = (cloudFile) => {
    const files = Array.isArray(cloudFile) ? cloudFile : [cloudFile];
    const successFiles = uploadedSuccessFiles?.filter(file => files.find(f => f.public_id !== file.public_id));

    /* Delete from local */
    setUploadedSuccessFiles(successFiles);

    /* Delete from redux */
    if (namespace) {
      const ids = files.map(file => file.public_id);
      dispatch(
        deleteFileUploadSuccess({
          ids,
          editorNamespace: namespace,
        })
      );
    }
    /* Update successful file numbers (for preload thumbnails) */
     handleSetTotalSuccessFilesCount(0);

    /* Delete from cloudinary */
    deleteCloudinaryFiles(files);
  };

  /* ------------------------------------------------------------------------------------- */
  /* --------------------- ERROR ACTIONS & ERROR NOTIFICATIONS --------------------------- */
  /* ------------------------------------------------------------------------------------- */

  /**
   * Upload files to cloudinary that exhibit errors, we push into the local error state.
   *
   * Note: Your error object must be in the following format:
   *     [{
   *       status: 'rejected',
   *       reason: { message: error },
   *     }]
   *     array<object>
   *
   * @param errors
   *
   */
  const handleUploadErrors = (errors) => {
    if (!Array.isArray(errors) || (Array.isArray(errors) && errors.length === 0)) return;
    const concatErrors = errors.map(error => error.reason?.message);

    /* 1. set it locally */
    setUploadErrorFiles(concatErrors);

    /* 2. set it globally. Probably not needed as we surface the error from here */
    setFileUploadReduxError(concatErrors);

    /* 3. Any errors: Log them */
    errors.forEach(error => {
      const err = error.reason?.message;
      logger('Cloudinary')(err);
    });
  };

  /* ------------------------------------------------------------------- */
  /* --------------------- API FILE UPLOADS  --------------------------- */
  /* ------------------------------------------------------------------- */

  /*
    Upload a single file

    private
  */
  const uploadFile = async (file, timestamp, signature) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('api_key', config.cloudinary.apiKey);
    formData.append('timestamp', timestamp);
    formData.append('signature', signature);
    formData.append('allowedFormats', ALLOWED_FILES.join(','));

    Object.entries(params).forEach(([key, value]) => {
      formData.append(key, value);
    });

    /* Uploading into cloudinary */
    return fetch(config.cloudinary.url, {
      method: 'POST',
      body: formData,
    })
      .then(response => response.text())
      .then(async data => {
        const parsedData = JSON.parse(data);

        // transform the image
        if (parsedData.resource_type === 'image') {
          const tranformedImage = await getAssetImagePath(parsedData);
          // eslint-disable-next-line no-underscore-dangle
          parsedData.transformed_image = tranformedImage;
        }

        /* Retrieval (fail or success) so no need to fake preload */
        handleSetTotalSuccessFilesCount();

        /* Update States Success */
        if (parsedData.error) {
          return Promise.reject(parsedData.error);
        }

        /* Store State */
        consolidateStateFileSuccessUpload(parsedData);

        return data;
      }).catch(e => {
        logger('Upload File: ')(e);
        return Promise.reject(e);
      });
  };

  /*
     Upload a multi files. This is the api you interface with.

     public
  */
  const uploadFiles = async (files) => {
    /* Get our signed token */
    generateCloudinaryAuthMutation.mutate(params, {
      /* Signed token response is good. Lets upload */
      onSuccess: async ({timestamp, signature}) => {
        /* Prelim error test against file size. Two objects {success, error } */
        const groupedFiles = getFilesGroupedByMaxFileSize(files);
        /* Promise of fetches on the success group */
        const promises = groupedFiles?.success.map(file => uploadFile(file, timestamp, signature));
        /* This tells us how many thumbnail placeholder loaders to have */
        const groupedFilesLength = groupedFiles?.success?.length ?? 0;

        handleSetTotalSuccessFilesCount(groupedFilesLength);

        setLoading(true);
        const allResponses = await Promise.allSettled(promises).then(resp => resp);

        /*
          Iterate thru the promises and gobble up all the errors
            1. Seed errors with any base file size errors
            2. api
       */
        const errors = allResponses
          .filter((result) => {
            return result.status === 'rejected';
          })
          .concat((groupedFiles?.error ?? []));

        /* State Assignments for errors */
        handleUploadErrors(errors);
        setLoading(false);
      },
      onError: handleUploadErrors,
    });
  };

  /* Handle Local Side Effects: Errors */
  useEffect(() => {
    if (uploadErrorFiles) {
      uploadErrorFiles.forEach((error) => {
        htToast(shortenStringByNumber(error, 70, '...'))
      })
    }

    setUploadErrorFiles(null);
  }, [uploadErrorFiles]);

  return {
    /* Image/Docs Assets that got uploaded, resolved. */
    uploadedSuccessFiles,
    /* Files that did not match requirement. Our case, size & api errors */
    uploadErrorFiles,
    /* external: Allow an interface to set errors from outside source: dragdrop */
    handleUploadErrors,
    /* Method to upload your chosen files from input */
    uploadFiles,
    /* Method to delete an entry from our state & cloudinary. string[] */
    handleDeleteFile,
    /* All the files (count) that are in our successful bucket, that are not resolved yet. We can use this if we want to display a loading or placeholder */
    totalSuccessFilesCount,
    /* Have all the selected files finished uploading to cloudinary? */
    cloudinaryLoading,
    /* If a user saves from the editor, we need to clear out the saves */
    handleRemoveSuccessFiles,
    /* If a user cancels from the editor (or wants to shut it all down), we need to clear the state and delete all from cloudinary */
    handleDeleteAllFromCloudinary,
    /* external: This is a standalone usage when transforming passed back File from the BE */
    handleTransformImage,
  };
};

export default useCloudinaryHooks;
